import { DateTime } from 'luxon'

import { AccountMetadataResponseDTO } from '@/core/api/dtos/response/accounts/account'
import { AccountOnboarding } from '@/core/api/entities/account/accountOnboarding'
import { BrandIdentity } from '@/core/api/entities/brandIdentity'
import { BusinessCategory } from '@/core/api/entities/businessCategory'
import { CulturalIdentity } from '@/core/api/entities/culturalIdentity'

import { BillingInfo } from '@core/api/entities/account/billingInfo'
import { MRR } from '@core/api/entities/account/mrr'
import {
	Subscription,
	SubscriptionStatus,
} from '@core/api/entities/account/subscription'
import { Invoice } from '@core/api/entities/invoice/invoice'
import { Royalty } from '@core/api/entities/misc/royalty'

export class Account {
	public id: number
	public parentId: number | null
	public billingDataId: number | null
	public uuid: string
	public name: string
	public publicRadios: boolean
	public country: string | null
	public planId: number | null
	public businessCategoryId: number | null
	public businessCategoryDescription: string | null
	public culturalIdentityId: number | null
	public demographicAges: string[]
	public demographicSocioeconomics: string[]
	public demographicGender: string | null
	public flags: string[]
	public accountType: string
	public multinationalBrand: boolean
	public totalCompanyStores: number
	public stripeCustomerId: string | null
	public acceptedGeneralTerms: boolean | null
	public acceptedParticularTerms: boolean | null
	public trialRemainingDays: number | null
	public termsSnapshot: any
	public canCreateTrials: boolean
	public features: string[]
	public source: string | null
	public deleted: boolean
	public suspended: boolean
	public test: boolean
	public createdAt: DateTime
	public updatedAt: DateTime
	public metadata: AccountMetadataResponseDTO | null
	public playersNotInstalled: number | null
	public playersOffline: number | null
	public playersOnline: number | null
	public availablePlayers: number
	public availableBoxes: number
	public playersCount: number | null = null
	public status: string | null
	public onboarding?: AccountOnboarding

	public mrr?: MRR
	public royalty?: Royalty
	public overdue?: Invoice[]
	public subscriptions?: Subscription[]
	public childrenAccounts?: Account[]
	public billingInfo?: BillingInfo
	public businessCategory?: BusinessCategory
	public culturalIdentity?: CulturalIdentity
	public brandIdentities?: BrandIdentity[]

	constructor(data: {
		id: number
		parentId: number | null
		billingDataId: number | null
		uuid: string
		name: string
		publicRadios: boolean
		country: string | null
		planId: number | null
		businessCategoryId: number | null
		businessCategoryDescription: string | null
		culturalIdentityId: number | null
		demographicAges: string[]
		demographicSocioeconomics: string[]
		demographicGender: string | null
		flags: string[]
		accountType: string
		multinationalBrand: boolean
		totalCompanyStores: number
		stripeCustomerId: string | null
		acceptedGeneralTerms: boolean | null
		acceptedParticularTerms: boolean | null
		trialRemainingDays: number | null
		termsSnapshot: any
		canCreateTrials: boolean
		features: string[]
		source: string | null
		deleted: boolean
		suspended: boolean
		test: boolean
		createdAt: DateTime
		updatedAt: DateTime
		metadata: AccountMetadataResponseDTO | null
		playersNotInstalled: number | null
		playersOffline: number | null
		playersOnline: number | null
		availablePlayers: number
		playersCount: number | null
		availableBoxes: number
		status: string | null
		onboarding?: AccountOnboarding
		mrr?: MRR
		royalty?: Royalty
		overdue?: Invoice[]
		subscriptions?: Subscription[]
		childrenAccounts?: Account[]
		billingInfo?: BillingInfo
		businessCategory?: BusinessCategory
		culturalIdentity?: CulturalIdentity
		brandIdentities?: BrandIdentity[]
	}) {
		this.id = data.id
		this.parentId = data.parentId
		this.billingDataId = data.billingDataId
		this.uuid = data.uuid
		this.name = data.name
		this.publicRadios = data.publicRadios
		this.country = data.country
		this.planId = data.planId
		this.businessCategoryId = data.businessCategoryId
		this.businessCategoryDescription = data.businessCategoryDescription
		this.culturalIdentityId = data.culturalIdentityId
		this.demographicAges = data.demographicAges
		this.demographicSocioeconomics = data.demographicSocioeconomics
		this.demographicGender = data.demographicGender
		this.flags = data.flags
		this.accountType = data.accountType
		this.multinationalBrand = data.multinationalBrand
		this.totalCompanyStores = data.totalCompanyStores
		this.stripeCustomerId = data.stripeCustomerId
		this.acceptedGeneralTerms = data.acceptedGeneralTerms
		this.acceptedParticularTerms = data.acceptedParticularTerms
		this.trialRemainingDays = data.trialRemainingDays
		this.termsSnapshot = data.termsSnapshot
		this.canCreateTrials = data.canCreateTrials
		this.features = data.features
		this.source = data.source
		this.deleted = data.deleted
		this.suspended = data.suspended
		this.test = data.test
		this.createdAt = data.createdAt
		this.updatedAt = data.updatedAt
		this.metadata = data.metadata
		this.playersNotInstalled = data.playersNotInstalled
		this.playersOffline = data.playersOffline
		this.playersOnline = data.playersOnline
		this.availablePlayers = data.availablePlayers
		this.playersCount = data.playersCount
		this.availableBoxes = data.availableBoxes
		this.status = data.status
		this.onboarding = data.onboarding
		this.mrr = data.mrr
		this.royalty = data.royalty
		this.overdue = data.overdue
		this.subscriptions = data.subscriptions
		this.childrenAccounts = data.childrenAccounts
		this.billingInfo = data.billingInfo
		this.businessCategory = data.businessCategory
		this.culturalIdentity = data.culturalIdentity
		this.brandIdentities = data.brandIdentities
	}

	hasAccessToFeature(feature: string): boolean {
		return this.features.includes(feature)
	}

	hasSchedulesFeature(): boolean {
		return this.hasAccessToFeature('schedules')
	}

	hasAdvertAudioVersionFeature(): boolean {
		return this.hasAccessToFeature('advert_audio_version')
	}

	hasSmartSlotsFeature(): boolean {
		return this.hasAccessToFeature('smart_slots')
	}

	hasFetchAllDataSelectorFeature(): boolean {
		return this.hasAccessToFeature('fetch_all_data_selector')
	}

	hasFetchAllDataDefaultFeature(): boolean {
		return this.hasAccessToFeature('fetch_all_data_default')
	}

	hasBrandProfileCompleted(): boolean {
		return (
			this.businessCategoryId !== null &&
			this.culturalIdentityId !== null &&
			(this.brandIdentities?.length || 0) > 0 &&
			(this.demographicAges?.length || 0) > 0 &&
			(this.demographicSocioeconomics?.length || 0) > 0 &&
			this.demographicGender !== null
		)
	}

	getActiveSubscriptions(): Subscription[] {
		return (
			this.subscriptions?.filter((subscription) =>
				[
					SubscriptionStatus.ACTIVE,
					SubscriptionStatus.FUTURE,
					SubscriptionStatus.IN_TRIAL,
				].includes(subscription.status)
			) || []
		)
	}

	getTrialSubscriptions(): Subscription[] {
		return (
			(this.subscriptions || []).filter((subscription) =>
				[SubscriptionStatus.IN_TRIAL].includes(subscription.status)
			) || []
		)
	}

	getSubscribableSubscriptions(): Subscription[] {
		return (
			(this.subscriptions || [])
				.filter((subscription) =>
					[SubscriptionStatus.ACTIVE, SubscriptionStatus.IN_TRIAL].includes(
						subscription.status
					)
				)
				.filter((subscription) => subscription.nextBillingAt === null) || []
		)
	}

	/**
	 * Check if the user is able to subscribe by checking current available subscriptions
	 */
	isAbleToSubscribe(): boolean {
		return this.getSubscribableSubscriptions().length > 0
	}

	// Return the remaining days for the traling period.
	getRemainingTrialDays(): number | null {
		const trialSubscriptions = this.getTrialSubscriptions()

		// Validate that there is at least one trial subscription and that the trial end date is not null
		if (
			trialSubscriptions.length === 0 ||
			trialSubscriptions[0].trialEndAt === null
		) {
			return null
		}

		// We've decided to round the number of days to the upper value always, so we can show the customer the remaining days
		// with this "additional day" just in sense of perception so we'll use the ceil function to calculate the remaining days
		return Math.ceil(
			trialSubscriptions[0].trialEndAt.diff(DateTime.now(), 'days').days
		)
	}

	getSoonerNextInvoiceDate(): DateTime | null {
		const filteredSubscriptions = (this.subscriptions || []).filter(
			(subscription) => subscription.nextBillingAt !== null
		)

		if (filteredSubscriptions.length === 0) {
			return null
		}

		return (
			filteredSubscriptions.sort(
				(a: Subscription, b: Subscription) =>
					a.nextBillingAt
						?.diff(b.nextBillingAt ?? DateTime.now(), 'minutes')
						.toMillis() || 0
			)[0]?.nextBillingAt || null
		)
	}

	static create(partial: Partial<Account>): Account {
		const defaults = {
			id: 0,
			parentId: null,
			billingDataId: null,
			culturalIdentityId: null,
			demographicAges: [],
			demographicSocioeconomics: [],
			demographicGender: null,
			brandIdentities: [],
			uuid: '',
			name: '',
			publicRadios: false,
			country: null,
			planId: null,
			businessCategoryId: null,
			businessCategoryDescription: null,
			flags: [],
			accountType: '',
			multinationalBrand: false,
			totalCompanyStores: 0,
			stripeCustomerId: null,
			acceptedGeneralTerms: null,
			acceptedParticularTerms: null,
			trialRemainingDays: null,
			termsSnapshot: null,
			canCreateTrials: false,
			features: [],
			source: null,
			deleted: false,
			suspended: false,
			test: false,
			createdAt: DateTime.now(),
			updatedAt: DateTime.now(),
			metadata: null,
			playersNotInstalled: null,
			playersOffline: null,
			playersOnline: null,
			availablePlayers: 0,
			availableBoxes: 0,
			status: null,
			playersCount: null,
		}

		return new Account({ ...defaults, ...partial })
	}
}
